import { Icon } from '@plone/volto/components';
import React, { FC, Fragment } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';

import kategorieSVG from '../../../icons/themen.svg';
import formatSVG from '../../../icons/themen.svg';
import statusSVG from '../../../icons/verifiziert.svg';
import typSVG from '../../../icons/favoriten.svg';
import fachbereichSVG from '../../../icons/fachbereich.svg';
import themenSVG from '../../../icons/medienmanager.svg';
import listCollapseSVG from '../../../icons/list_collapse.svg';
import { RootState } from '../../../store';

export interface FacetMetadataObject<K extends string = string> {
  id: K;
  name: string;
  icon: React.ReactNode;
}

export interface FacetMetadata {
  readonly fallback: FacetMetadataObject<'undefined'>;
  readonly data: FacetMetadataObject[];

  getIcon(idOrIndex: string | number): FacetMetadataObject['icon'];
  getCategory(idOrIndex: string | number): FacetMetadataObject;
}

export function getIcon(
  this: FacetMetadata,
  idOrIndex: string | number,
): FacetMetadataObject['icon'] {
  return getCategory.call(this, idOrIndex).icon;
}
export function getCategory(
  this: FacetMetadata,
  idOrIndex: string | number,
): FacetMetadataObject {
  return (
    (typeof idOrIndex === 'number'
      ? this.data[idOrIndex]
      : this.data.find(({ id }) => id === idOrIndex)) ?? this.fallback
  );
}

export const facetMetadata: FacetMetadata = Object.freeze({
  fallback: {
    id: 'undefined',
    name: 'undefined',
    icon: kategorieSVG,
  },
  data: [
    {
      id: 'category',
      name: 'Kategorie',
      icon: kategorieSVG,
    },
    {
      id: 'review_state',
      name: 'Status',
      icon: statusSVG,
    },
    {
      id: 'types',
      name: 'Typ',
      icon: typSVG,
    },
    {
      id: 'departments',
      name: 'Fachbereich',
      icon: fachbereichSVG,
    },
    {
      id: 'topics',
      name: 'Themen',
      icon: themenSVG,
    },
    {
      id: 'mimetype',
      name: 'Format',
      icon: formatSVG,
    },
  ],
  getIcon,
  getCategory,
} as FacetMetadata);

export interface FacetFilterProps {
  facets: Facets;
  selectedFacets: SelectedFacets;
  metadata?: FacetMetadata;
  toggleFacet: (facetId: keyof Facets, facetValueId: FacetValue['id']) => void;
  expanded: boolean;
  toggleExpanded: () => void;
}

const FacetFilter: FC<FacetFilterProps> = ({
  facets,
  selectedFacets,
  metadata = facetMetadata,
  toggleFacet,
  expanded,
  toggleExpanded,
}) => {
  // @ts-ignore
  const category = useSelector(
    (state: RootState) => state.router?.location?.query?.category,
  );
  // on phones the filters are collapsed until the button expands them, on
  // larger screens the button is hidden (#19308, #22799)
  const hasFacets = Object.keys(facets).length > 0;

  return (
    <>
      {hasFacets && (
        <button
          type="button"
          className={cx('ui button expand-filtering-button', { expanded })}
          aria-expanded={expanded}
          onClick={toggleExpanded}
        >
          Filter auswählen
          <Icon name={listCollapseSVG} size="20px" />
        </button>
      )}
      <div
        className={cx('facet-filter', { expanded })}
        data-category={category}
      >
        {Object.entries(facets).map(([facetId, facetValues]) => (
          <Fragment key={facetId}>
            <FacetTitle category={metadata.getCategory(facetId)} />
            <div
              className="facet-values"
              data-facet-values={facetId}
              data-has-active={
                facetId in selectedFacets && selectedFacets[facetId].length > 0
              }
            >
              {Object.values(facetValues).map((value) => (
                <FacetValueComponent
                  key={value.id}
                  value={value}
                  active={
                    facetId in selectedFacets &&
                    selectedFacets[facetId].includes(value.id)
                  }
                  facetId={facetId}
                  toggleFacet={toggleFacet}
                />
              ))}
            </div>
          </Fragment>
        ))}
      </div>
    </>
  );
};

export interface FacetTitleProps {
  category?: FacetMetadataObject;
}

export const FacetTitle: FC<FacetTitleProps> = ({ category }) => {
  if (category != null) {
    return (
      <div className="facet-title" data-facet-title={category.name}>
        <Icon name={category.icon} size="1em" />
        {category.name}
      </div>
    );
  }
  return null;
};

export interface FacetValueProps {
  value: FacetValue;
  facetId: keyof Facets;
  active: boolean;
  toggleFacet: (facetId: keyof Facets, facetValueId: FacetValue['id']) => void;
}

export const FacetValueComponent: FC<FacetValueProps> = ({
  value,
  facetId,
  active,
  toggleFacet,
}) => (
  <button
    data-active={active}
    disabled={value.count <= 0}
    className="facet-value"
    onClick={() => toggleFacet(facetId, value.id)}
  >
    {value.title}
    <span>({Math.max(value.count, 0)})</span>
  </button>
);

export default FacetFilter;

export type Facets = Record<string, FacetValue[]>;

export interface FacetValue {
  id: string;
  title: string;
  count: number;
}

export type SelectedFacets = Record<string, Array<FacetValue['id']>>;
